import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {DomSanitizer} from '@angular/platform-browser';

import {TranslateService} from '@ngx-translate/core';

import {BehaviorSubject, EMPTY, from, Observable, of} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, finalize, switchMap, takeUntil, tap} from 'rxjs/operators';

import {ToastrService} from 'ngx-toastr';
import {DATE_MAX} from '../../../app.constants';
import {Destroyable} from '../../../destroyable.component';
import {CliEmpAttributes, EmpContractAttributes} from '../../../models/models.interfaces';
import {Document, EmployeeDocument, Template} from '../../../shared/documents/documents.interface';
import {GpDocumentEditorComponent} from '../../../template-management/template-editor/gp-document-editor.component';
import {ETemplate} from '../../../template-management/template-management.interfaces';
import {ApiService, Data, DataListAndCount, Link} from '../../api';
import {DocumentsService} from '../../documents/documents.service';
import {ContractStatus} from '../../employees/contract/contract.interfaces';
import {Contract, Employee, SearchOptions, SelectEmployee} from '../../employees/employees.model';
import {EmployeesService} from '../../employees/employees.service';
import {FileDescription} from '../../files/files.model';
import {FieldType} from '../../form-generator/fields/fields.interfaces';
import {FormValuesWrapper, GroupZoneDescriptor, ZoneValues} from '../../form-generator/form-generator.interfaces';
import {GeneratedSimpleFormComponent} from '../../form-generator/generated-simple-form/generated-simple-form.component';
import {Validators as GpValidators} from '../../forms';
import {ModalComponent} from '../../modal';
import {DateLocalizationPipe} from '../../pipes/date-localization.pipe';
import {TreeNode} from '../../ui';
import {User} from '../../user';
import {FileModel, Files, getFormatByMIME, Preview} from '../../utils/files';
import {getInitials, isValidPhotoUrl} from '../../utils/strings';
import {ProcessInstance} from '../../workflow/workflow-v2.interface';
import {getWorkflowKey, SEND_DOCUMENT, WorkflowV2Service} from '../../workflow/workflow-v2.service';

export enum ERRORS {
  SAVING = 'SAVING',
  PREVIEWING = 'PREVIEWING',
  SENDING = 'SENDING',
  NOT_SUPPORTED_FORMAT = 'NOT_SUPPORTED_FORMAT',
  LIMIT_REACHED = 'LIMIT_REACHED'
}

enum LOADING {
  SAVE = 'SAVE',
  PREVIEW = 'PREVIEW',
  PREVIEW_DOCUMENT_EDITOR = 'PREVIEW_DOCUMENT_EDITOR'
}

export enum PREVIEW_STATE {
  PREVIEW_IN_PDF_FORMAT = 'PREVIEW_IN_PDF_FORMAT',
  PREVIEW_IN_PDF_FORMAT_WITHOUT_SAVE = 'PREVIEW_IN_PDF_FORMAT_WITHOUT_SAVE',
  PREVIEW_IN_DOCUMENT_EDITOR = 'PREVIEW_IN_DOCUMENT_EDITOR'
}

export enum DISCARD_CONFIRMATION_TRIGGERS {
  PREVIEW_RESET = 'PREVIEW_RESET',
  MODAL_CLOSE = 'MODAL_CLOSE',
  INPUT_CHANGE = 'INPUT_CHANGE',
  PARAMETERS_CHANGE = 'PARAMETERS_CHANGE'
}

@Component({
  selector: 'gp-generate-document-modal',
  templateUrl: './generate-document-modal.component.html',
  styleUrls: ['./generate-document-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GenerateDocumentModalComponent extends Destroyable implements OnInit {
  private readonly RAW_PREFIX = 'raw:';

  @ViewChild(GeneratedSimpleFormComponent, {static: true})
  public generatedSimpleForm: GeneratedSimpleFormComponent;

  @ViewChild(GpDocumentEditorComponent)
  public documentEditorComponent: GpDocumentEditorComponent;

  public contractId: number;
  public employee: Employee;
  public cliRegId: number;
  public formValuesWrapper: FormValuesWrapper;
  public eTemplateData: ETemplate;
  public connectedUser: User;
  public modalTitle: string;

  public selectedTemplateDescriptor: GroupZoneDescriptor;
  public selectedTemplateValues: ZoneValues;
  public emailForm: UntypedFormGroup;
  public sendForm: UntypedFormGroup;
  public documentDetailsForm: UntypedFormGroup;
  public selectedPreview: Preview;
  public isCollapsedByBloc: boolean[] = [false, true, true];
  private hiddenValues: Map<any, any> = new Map<any, any>();
  private readonly debugReportTiming: boolean;
  private readonly debugReportForceXmx: boolean;
  public selectedTemplate: Template;
  public mvelAliases: string[];
  public emailMvelAliases: string[];

  private currentFileName = '';
  private documentTitle = '';


  public eTemplateDocumentEditorData: ETemplate;
  private currentDocument: FileModel;

  public isDocumentPreviewInProgress = false;
  public isDocumentPreviewedInEditor = false;
  public isPreviewedDataModifiedInEditor = false;
  public showConfirmDiscardChangesMessage = false;

  public errorStatus: ERRORS | '' = '';
  public loadingStatus: LOADING | '' = '';
  public discardEditorChangeTrigger: DISCARD_CONFIRMATION_TRIGGERS | '' = '';
  public currentPreviewState: PREVIEW_STATE | '' = PREVIEW_STATE.PREVIEW_IN_PDF_FORMAT;
  public editorBlobAsString: string;
  private treeNodesTemplates: Array<TreeNode<Template>>;

  public employeesList: SelectEmployee[] = [];
  public isEmployeesListLoading = false;
  private page = 0;
  private perPage = 25;
  private employeesListSearchItem$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  constructor(
    public api: ApiService,
    private cd: ChangeDetectorRef,
    public documentsService: DocumentsService,
    private sanitizer: DomSanitizer,
    private fb: UntypedFormBuilder,
    private detectorRef: ChangeDetectorRef,
    private toastrService: ToastrService,
    private translate: TranslateService,
    private workflowV2Service: WorkflowV2Service,
    private employeesService: EmployeesService,
    private dateLocalizationPipe: DateLocalizationPipe) {
    super();

    this.emailForm = this.fb.group({
      sendEmail: [false],
      email: [{value: '', disabled: true}, [Validators.required, GpValidators.emailValidator]]
    });
    this.sendForm = this.fb.group({
      send: [{value: false, disabled: true}],
      comment: [''],
      receiver: [{value: '', disabled: true}, [Validators.required]]
    });
    this.documentDetailsForm = this.fb.group({
      template: ['', [Validators.required]],
      employee: [{value: '', disabled: true}],
      contractId: ['', []],
      title: ['', []],
      send: this.sendForm,
      email: this.emailForm
    });

    this.emailForm.get('sendEmail').valueChanges.pipe(
      tap(value => {
        const emailControl = this.emailForm.get('email');
        if (value) {
          emailControl.enable();
          if (!emailControl.value) {
            this.emailMvelAliases = ['personalEmail'];
          }
        } else {
          emailControl.disable();
        }
      }),
      takeUntil(this.componentDestroyed$)
    )
      .subscribe();
    this.debugReportTiming = localStorage.getItem('gp_debug_report_timing') === 'true';
    this.debugReportForceXmx = localStorage.getItem('gp_debug_report_force_xmx') === 'true';
  }

  public get isLoadingSave(): boolean {
    return this.loadingStatus === LOADING.SAVE;
  }

  public get isLoadingPreview(): boolean {
    return this.loadingStatus === LOADING.PREVIEW;
  }

  public get isLoadingPreviewInDocumentEditor(): boolean {
    return this.loadingStatus === LOADING.PREVIEW_DOCUMENT_EDITOR;
  }

  public get errorInSaving(): boolean {
    return this.errorStatus === ERRORS.SAVING;
  }

  public get errorInPreviewing(): boolean {
    return this.errorStatus === ERRORS.PREVIEWING;
  }

  public get errorLimitReached(): boolean {
    return this.errorStatus === ERRORS.LIMIT_REACHED;
  }


  public get sendOption(): boolean {
    return this.sendForm.get('send').value;
  }

  public get errorInSending(): boolean {
    return this.errorStatus === ERRORS.SENDING;
  }

  public get errorNotSupportedFormat(): boolean {
    return this.errorStatus === ERRORS.NOT_SUPPORTED_FORMAT;
  }

  public get previewInPdfFormat(): boolean {
    return this.currentPreviewState === PREVIEW_STATE.PREVIEW_IN_PDF_FORMAT;
  }

  public get previewInPdfFormatWithoutSave(): boolean {
    return this.currentPreviewState === PREVIEW_STATE.PREVIEW_IN_PDF_FORMAT_WITHOUT_SAVE;
  }

  public get previewInDocumentEditor(): boolean {
    return this.currentPreviewState === PREVIEW_STATE.PREVIEW_IN_DOCUMENT_EDITOR;
  }

  public get isPreviewButtonDisabled(): boolean {
    return (!this.selectedTemplate || this.generatedSimpleForm.form?.invalid
      || this.documentDetailsForm?.invalid || this.isDocumentPreviewedInEditor
      || this.isDocumentPreviewInProgress);
  }

  private get previewDocument$(): Observable<FileModel> {
    return this.documentsService?.generateDocument(
      {
        cliRegId: this.cliRegId,
        name: this.documentDetailsForm.get('template').value,
        contractId: this.contractId.toString(),
        engine: this.selectedTemplate.engine,
        locale: this.selectedTemplate.locale,
        arguments: this.prepareRequestBody()
      });
  }

  public reset(): void {
    this.discardEditorChangeTrigger = DISCARD_CONFIRMATION_TRIGGERS.PREVIEW_RESET;
    this.showConfirmDiscardChangesMessage = true;
  }

  public preview(modal: ModalComponent): void {
    if (!this.generatedSimpleForm.form.valid && this.selectedTemplate.arguments) {
      return;
    }

    if (this.selectedTemplate?.engine === 'REPORT' || this.previewInPdfFormatWithoutSave) {
      this.generateDocument(modal);
    } else {
      this.previewTemplateInEditor();
    }
  }

  public save(modal: ModalComponent, send: boolean): void {
    if (!this.generatedSimpleForm.form.valid && this.selectedTemplate.arguments) {
      return;
    }

    this.loadingStatus = LOADING.SAVE;

    this.handleSave(send).pipe(
      tap(() => {
        this.loadingStatus = '';
        modal.close();
        this.documentsService.refreshDocumentList$.next(true);

        const documentName = this.getDocumentName(this.currentDocument?.type);
        this.displayDocumentSavedToastr(documentName, send);
      }),
      catchError(() => {
        this.loadingStatus = '';
        this.errorStatus = ERRORS.SAVING;
        return EMPTY;
      }),
      finalize(() => this.cd.detectChanges()),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  public allowPreviewReset(): void {
    this.isPreviewedDataModifiedInEditor = true;
    this.cd.detectChanges();
  }

  public collapseBlock(index: number): void {
    if (this.selectedTemplate) {
      this.isCollapsedByBloc[index] = !this.isCollapsedByBloc[index];
    }
  }

  public templateClicked(): void {
    if (this.isPreviewedDataModifiedInEditor) {
      this.discardEditorChangeTrigger = DISCARD_CONFIRMATION_TRIGGERS.INPUT_CHANGE;
      this.showConfirmDiscardChangesMessage = true;
    }
  }

  public downloadEditorContent(): void {
    this.getEditorContentAsBlob()
      .pipe(
        tap((blob: Blob) => {
          if (blob) {
            Files.downloadBlob(blob, this.documentTitle, false);
          } else {
            this.errorStatus = ERRORS.PREVIEWING;
          }
        }),
        finalize(() => this.refreshView()),
        takeUntil(this.componentDestroyed$)
      ).subscribe();
  }

  public previewTemplateInEditor(): void {
    this.resetEditorPreviewStates();
    this.isDocumentPreviewInProgress = true;

    if (this.selectedTemplate?.engine === 'SYNCFUSION') {
      this.setViewStateToPreviewDocumentEditor();
      this.loadingStatus = LOADING.PREVIEW_DOCUMENT_EDITOR;
      this.renderEditorWithPreviewData();
    }
  }

  public parametersChanged(rawValues): void {
    const selectedTemplate = this.selectedTemplate?.name;
    if (this.selectedTemplate?.engine === 'SYNCFUSION' &&
      rawValues[selectedTemplate].length &&
      rawValues[selectedTemplate][0]?.values?.documentGenerationDate) {
      if (this.isPreviewedDataModifiedInEditor) {
        this.discardEditorChangeTrigger = DISCARD_CONFIRMATION_TRIGGERS.PARAMETERS_CHANGE;
        this.showConfirmDiscardChangesMessage = true;
      } else {
        this.selectedPreview = null;
        this.resetEditorPreviewStates();
      }
    }
  }

  ngOnInit(): void {
    this.treeNodesTemplates = Object.values(this.formValuesWrapper?.data)
      .map((node: TreeNode<Template>) => node.children)
      .flatMap(node => node);

    if (this.previewInPdfFormatWithoutSave) {
      this.documentDetailsForm.get('template').setValue(this.eTemplateData.name);
      this.documentDetailsForm.get('template').disable();
      this.documentDetailsForm.get('contractId').setValidators([Validators.required]);

      this.selectedTemplate = this.treeNodesTemplates?.find(node => node.value === this.eTemplateData?.name)?.data;
      this.selectedTemplateDescriptor = this.formValuesWrapper?.descriptors.get(this.eTemplateData?.name);
      this.selectedTemplateValues = this.formValuesWrapper?.zonesValues.get(this.eTemplateData?.name)[0];
      this.isCollapsedByBloc[1] = false;

      this.cliRegId = this.eTemplateData.cliRegId;

      this.documentDetailsForm.get('contractId').valueChanges.pipe(
        tap((contractId: number) => {
          this.contractId = contractId;
        }),
        takeUntil(this.componentDestroyed$)
      ).subscribe();

      this.detectorRef.detectChanges();
      this.setDefaultValuesIfNecessary(this.RAW_PREFIX);

      this.employeesListSearchItem$.asObservable()
        .pipe(
          debounceTime(350),
          distinctUntilChanged(),
          tap(() => this.isEmployeesListLoading = true),
          switchMap((searchTerm) => this.fetchEmployees(searchTerm)),
          tap((response: DataListAndCount<CliEmpAttributes>) => {
            this.employeesList = this.generateSelectEmployeeList(response.items?.data);
            this.isEmployeesListLoading = false;
            this.cd.markForCheck();
          })
        ).subscribe();

    } else {
      this.hiddenValues.set('contractId', this.contractId);
      this.hiddenValues.set('employeeId', this.employee?.id);
      this.documentDetailsForm.get('title').setValidators([Validators.required]);

      this.documentDetailsForm.get('employee').setValue(this.employee);
      this.sendForm.get('receiver').setValue(this.employee);
      this.documentDetailsForm.get('template').valueChanges
        .pipe(
          tap((templateName: string) => {
            this.resetData();
            this.documentDetailsForm.get('title').reset();
            this.currentPreviewState = PREVIEW_STATE.PREVIEW_IN_PDF_FORMAT;

            if (templateName) {
              this.selectedTemplate = this.treeNodesTemplates?.find(node => node.value === templateName)?.data;

              this.documentTitle = [this.selectedTemplate?.name, this.employee?.firstName, this.employee?.lastName].join('_');
              this.documentDetailsForm.get('title').setValue(this.documentTitle);
              this.sendForm.get('send').enable();

              this.selectedTemplateDescriptor = this.formValuesWrapper?.descriptors.get(templateName);
              this.selectedTemplateValues = this.formValuesWrapper?.zonesValues.get(templateName)[0];

              this.isCollapsedByBloc[1] = false;
              this.detectorRef.detectChanges();
              this.setDefaultValuesIfNecessary(this.RAW_PREFIX);
            }
          }),
          takeUntil(this.componentDestroyed$)
        ).subscribe();

      this.documentDetailsForm.get('title').valueChanges.pipe(
        tap((documentTitle: string) => {
          this.documentTitle = documentTitle;
          this.errorStatus = '';
        }),
        takeUntil(this.componentDestroyed$)
      ).subscribe();
    }

    this.documentDetailsForm.updateValueAndValidity();
  }

  public handleModalClose(modal): void {
    if (this.isPreviewedDataModifiedInEditor) {
      this.discardEditorChangeTrigger = DISCARD_CONFIRMATION_TRIGGERS.MODAL_CLOSE;
      this.showConfirmDiscardChangesMessage = true;
    } else {
      modal.close();
    }
  }

  public filterEmployees(keyword: string): void {
    this.employeesListSearchItem$.next(keyword);
  }

  private fetchEmployees(searchTerm: string): Observable<DataListAndCount<CliEmpAttributes>> {
    const options: SearchOptions = {
      ftsSearch: searchTerm,
      page: this.page,
      perPage: this.perPage,
      orderBy: 'lastName,firstName',
      seeInactiveContracts: true,
      seeInactiveEmployees: true
    };
    return this.employeesService.getAllEmployees(options);
  }

  private generateSelectEmployeeList(employeeList: Data<CliEmpAttributes>[]): SelectEmployee[] {
    return employeeList
      .map((element: Data<CliEmpAttributes>) => {
        const empMainContract = element?.attributes?.empContracts?.find((empContract: EmpContractAttributes) => empContract.mainContract);
        if (empMainContract) {
          const name = `${element?.attributes.lastName.toUpperCase()} ${element?.attributes.firstName}`;
          const avatarUrl = element.links.find((link: Link) => link.rel === 'photo');
          return {
            id: empMainContract.id,
            text: this.generateSelectEmployee(name, avatarUrl, empMainContract),
          };
        }
      });
  }

  private generateSelectEmployee(name: string, avatarUrl?: Link, empContract?: Contract): string {
    const hasPhoto = isValidPhotoUrl(avatarUrl?.href);
    const parts = name.trim().split(/\s+/);
    const initials = getInitials(parts[0], parts.length > 1 ? parts[parts.length - 1] : '');
    const avatar = hasPhoto
      ? `<span class="circle avatar avatar-text w-32">
            <img src="${this.api.apiHost + avatarUrl.href}" alt="">
        </span>`
      : `<span class="gp-initials-avatar gp-avatar-md">${initials}</span>`;

    const contractName = this.translate.instant('EMPLOYEE_FORM.Contract_name', {
      name: empContract.cliRegNickName
    });

    const cliRegFlagUrl = empContract.links?.find((link: Link) => link?.rel === 'cliRegFlag');
    const flagImg = cliRegFlagUrl?.href
      ? `<img class="flag-icon" src="${this.api.apiHost + cliRegFlagUrl.href}" alt=""/>`
      : '';

    return `<div class="emp">
              <div class="emp-avatar">
                    ${avatar}
              </div>
              <div class="emp-info">
                <div>${name}</div>
                <small class="text-muted">
                 ${flagImg}
                <span class="m-r-xs">${contractName}</span>
                <span>${this.getContractDatePeriodAsText(empContract)} ${this.getContractStatusToDisplay(empContract)}</span>
                </small>
            </div>
          </div>`;
  }

  private getContractDatePeriodAsText(contract: Contract): string {
    const beginDate = this.dateLocalizationPipe.transform(contract.begindate);
    const endDate = (contract.enddate === DATE_MAX || !contract.enddate) ? '...' : this.dateLocalizationPipe.transform(contract.enddate);
    return `(${beginDate} - ${endDate})`;
  }

  private getContractStatusToDisplay(empContract: Contract): string {
    if (!empContract.status) {
      return '';
    }
    let statusToDisplay = ' - ';

    if (empContract.status === ContractStatus.ACTIVE) {
      statusToDisplay += this.translate.instant('EMPLOYEE_MANAGE.employee_status_active');
    } else if (empContract.status === ContractStatus.INACTIVE) {
      statusToDisplay += this.translate.instant('EMPLOYEE_MANAGE.employee_status_inactive');
    } else if (empContract.status === ContractStatus.FUTURE) {
      statusToDisplay += this.translate.instant('EMPLOYEE_MANAGE.employee_status_future');
    }
    return statusToDisplay;
  }

  private setDefaultValuesIfNecessary(prefix: string): void {
    this.mvelAliases = this.selectedTemplateDescriptor?.zones
      .filter(z => z.defaultValue && !z.defaultValue.startsWith(this.RAW_PREFIX))
      .map(z => z.defaultValue);
    for (const zone of this.selectedTemplateDescriptor.zones) {
      // handle raw default values if no prefix specified or defaultValue starting with prefix when specified
      if (zone.defaultValue && (!prefix || zone.defaultValue.startsWith(prefix))) {
        const defaultValue = zone.defaultValue.replace(new RegExp('^' + prefix), '');
        const isComplexType = [FieldType.ENTITY, FieldType.ENTITY_DATA, FieldType.ENTITY_PREFIXED,
          FieldType.LIST, FieldType.TREE, FieldType.PEOPLE_TREE]
          .find(value => value === FieldType[zone.type]);
        if (isComplexType) {
          try {
            const complexDefaultValue = JSON.parse(defaultValue);
            if (!complexDefaultValue || !complexDefaultValue.value || !complexDefaultValue.transId) {
              throw new Error('bad default value format');
            }
            this.selectedTemplateValues.values[zone.zoneID] = complexDefaultValue;
          } catch (ignored) {
            console.warn('Failed to set default value on ' + zone.type + ' zone, bad format (value/transId required): ' + defaultValue);
          }
        } else {
          this.selectedTemplateValues.values[zone.zoneID].value = defaultValue;
        }
      }
    }
  }

  private renderEditorWithPreviewData(): void {
    this.documentsService?.previewDocument(
      {
        cliRegId: this.cliRegId,
        name: this.documentDetailsForm.get('template').value,
        contractId: this.contractId.toString(),
        engine: this.selectedTemplate.engine,
        locale: this.selectedTemplate.locale,
        arguments: this.prepareRequestBody()
      }).pipe(
      tap((response) => {
        this.isDocumentPreviewedInEditor = true;
        this.eTemplateDocumentEditorData = response;
      }),
      catchError((error) => {
        if (error.status === 402) {
          this.errorStatus = ERRORS.LIMIT_REACHED;
        } else {
          this.errorStatus = ERRORS.PREVIEWING;
        }
        this.eTemplateDocumentEditorData = null;
        return EMPTY;
      }),
      finalize(() => {
        this.loadingStatus = '';
        this.isDocumentPreviewInProgress = false;
        this.isPreviewedDataModifiedInEditor = false;
        this.refreshView();
      }),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  private handleSave(send: boolean): Observable<ProcessInstance | FileDescription> {
    let data$: Observable<FileModel>;

    if (this.selectedTemplate?.engine === 'SYNCFUSION' && this.isDocumentPreviewedInEditor) {
      data$ = this.documentFromPreviewContent$;
    } else {
      data$ = this.previewDocument$;
    }

    return data$.pipe(
      switchMap((document: FileModel) => {
        this.currentDocument = document;
        return this.uploadDocument(document, send);
      })
    );
  }

  private displayDocumentSavedToastr(documentTitle: string, published: boolean): void {
    const messageKey = published ? 'DOCUMENTS.document_has_been_saved_and_published' : 'DOCUMENTS.document_has_been_saved';
    this.translate.get(messageKey, {document: documentTitle})
      .pipe(
        tap((message: string) => this.toastrService.success(message, '', {timeOut: 7000})),
        takeUntil(this.componentDestroyed$)
      ).subscribe();
  }

  private getDocumentName(documentType: string): string {
    const fileExtension = getFormatByMIME(documentType);
    return [this.documentTitle, fileExtension].join('.');
  }

  public handleDiscardEditorChangesConfirmation(modal): void {
    switch (this.discardEditorChangeTrigger) {
      case DISCARD_CONFIRMATION_TRIGGERS.PREVIEW_RESET:
        this.previewTemplateInEditor();
        break;
      case DISCARD_CONFIRMATION_TRIGGERS.MODAL_CLOSE:
        modal.close();
        break;
      case DISCARD_CONFIRMATION_TRIGGERS.INPUT_CHANGE:
        this.documentDetailsForm.get('template').reset();
        this.documentDetailsForm.get('title').reset();
        this.resetData();
        this.currentPreviewState = PREVIEW_STATE.PREVIEW_IN_PDF_FORMAT;
        break;
      case DISCARD_CONFIRMATION_TRIGGERS.PARAMETERS_CHANGE:
        this.errorStatus = '';
        this.loadingStatus = '';
        this.discardEditorChangeTrigger = '';
        this.resetViewState();

        this.resetEditorPreviewStates();
        this.selectedPreview = null;
        this.currentPreviewState = PREVIEW_STATE.PREVIEW_IN_PDF_FORMAT;
        break;
    }
  }

  private uploadDocument(document: FileModel, send: boolean): Observable<ProcessInstance | FileDescription> {
    const familyName = this.selectedTemplate.name;

    const payload = {
      tags: {
        firstName: this.connectedUser?.firstName,
        lastName: this.connectedUser?.lastName,
        id: this.connectedUser?.id,
        modified: this.isPreviewedDataModifiedInEditor
      },
      type: 'EMPLOYEE_DOCUMENT',
      storageType: 'INTERNAL',
      status: 'INITIATED',
      exportableToNifi: false,
      storedCompressed: false,
      contractId: this.contractId,
      employeeId: this.employee?.id,
      name: this.getDocumentName(document?.type),
      family: familyName,
      subFamily: familyName,
      ...(document.signatureData ? {signatureData: document.signatureData} : {})
    };

    return this.documentsService.uploadDocument(document.blob, {...payload}).pipe(
      switchMap((employeeDocument: EmployeeDocument) => {
        if (send) {
          const workflowKey = getWorkflowKey(SEND_DOCUMENT, this.cliRegId);
          const request = {
            request: {
              contractId: this.contractId,
              cliEmpId: this.employee.id,
              documentId: employeeDocument.id,
              documentName: employeeDocument.name,
              recipientEmail: this.emailForm.get('sendEmail').value ? this.emailForm.get('email').value : null,
              comment: this.sendForm.get('comment').value
            },
            cliRegId: this.cliRegId
          };
          return this.workflowV2Service.startProcessInstance(workflowKey, request, true)
            .pipe(catchError(() => {
              this.errorStatus = ERRORS.SENDING;
              this.loadingStatus = '';
              this.selectedPreview = null;
              this.documentsService.refreshDocumentList$.next(true);
              return EMPTY;
            }));
        }
        return of(employeeDocument);
      })
    );
  }

  private setViewStateToPreviewDocumentEditor(): void {
    this.resetViewState();
    this.currentPreviewState = PREVIEW_STATE.PREVIEW_IN_DOCUMENT_EDITOR;
    this.showConfirmDiscardChangesMessage = false;
  }

  private resetEditorPreviewStates(): void {
    this.eTemplateDocumentEditorData = null;
    this.isDocumentPreviewInProgress = false;
    this.isDocumentPreviewedInEditor = false;
    this.isPreviewedDataModifiedInEditor = false;
    this.showConfirmDiscardChangesMessage = false;
  }

  private resetData(): void {
    this.selectedTemplateDescriptor = null;
    this.selectedTemplateValues = null;
    this.selectedTemplate = null;
    this.selectedPreview = null;
    this.mvelAliases = null;
    this.resetViewState();
    this.errorStatus = '';
    this.loadingStatus = '';
    this.discardEditorChangeTrigger = '';
    this.resetEditorPreviewStates();
    this.sendForm.get('comment').reset();
    this.sendForm.get('send').setValue(false);
    this.sendForm.get('send').disable();
    this.emailForm.reset();
    this.isCollapsedByBloc[2] = true;
  }

  private get documentFromPreviewContent$(): Observable<FileModel> {
    return this.getEditorContentAsBlob()
      .pipe(
        switchMap((blob: Blob) => Files.blobToBase64(blob)),
        switchMap((base64String: string) => {
          const payload: Document = this.preparePayloadForGenerateFromPreviewContent(base64String);
          return this.documentsService.generateFromPreviewContent(payload);
        })
      );
  }

  private get generateFromPreviewContent$(): Observable<FileModel> {
    const payload: Document = this.preparePayloadForGenerateFromPreviewContent(this.editorBlobAsString);
    return this.documentsService.generateFromPreviewContent(payload);
  }

  private preparePayloadForGenerateFromPreviewContent(editorBlobAsString: string): Document {
    return {
      cliRegId: this.cliRegId,
      name: this.documentDetailsForm.get('template').value,
      contractId: this.contractId.toString(),
      engine: this.selectedTemplate.engine,
      locale: this.selectedTemplate.locale,
      arguments: this.prepareRequestBody(),
      value: editorBlobAsString,
      ...(this.eTemplateData?.signatureData?.length ? {signatureData: this.eTemplateData.signatureData} : {})
    };
  }

  private getDocumentPreview(certificateHref: string, fileName: string): Preview {
    return {
      iframe: this.sanitizer.bypassSecurityTrustHtml(`
             <iframe width="100%" height="100%" src="${certificateHref}"
                     name="${fileName}">
             </iframe>
          `),
      href: this.sanitizer.bypassSecurityTrustUrl(certificateHref),
      download: fileName
    };
  }

  private refreshView(): void {
    if (!this.isDestroyed) {
      this.detectorRef.detectChanges();
    }
  }

  private prepareRequestBody(): { name: string, type: string, transId: string, value: any, defaultValue: any }[] {
    const args = this.generatedSimpleForm.convertToArguments(this.hiddenValues) || [];

    if (this.debugReportTiming) {
      args.push({
        name: 'DEBUG_TIMING',
        type: 'BOOLEAN',
        transId: 'DEBUG_TIMING',
        value: true,
        defaultValue: undefined
      });
    }
    if (this.debugReportForceXmx) {
      args.push({
        name: 'DEBUG_FORCE_XMX',
        type: 'LABEL',
        transId: 'DEBUG_FORCE_XMX',
        value: window.prompt('DEBUG_FORCE_XMX'),
        defaultValue: undefined
      });
    }
    return args;
  }

  private getEditorContentAsBlob(): Observable<Blob> {
    return from(this.documentEditorComponent?.documentEditorContainerComponent.documentEditor.saveAsBlob('Docx'));
  }

  private generateDocument(modalRef: ModalComponent): void {
    let data$: Observable<FileModel>;
    this.loadingStatus = LOADING.PREVIEW;

    if (this.previewInPdfFormat) {
      data$ = this.previewDocument$;
    } else if (this.previewInPdfFormatWithoutSave) {
      data$ = this.generateFromPreviewContent$;
    }

    data$
      .pipe(
        tap((fileModel: FileModel) => {
          if (fileModel) {
            this.currentFileName = fileModel.fileName;

            if ((navigator as any).msSaveBlob) {
              (navigator as any).msSaveBlob(fileModel.blob, fileModel.fileName);
              modalRef.close();
            } else {
              this.errorStatus = fileModel.type !== 'application/pdf' ? ERRORS.NOT_SUPPORTED_FORMAT : '';
              this.selectedPreview = this.getDocumentPreview(URL.createObjectURL(fileModel.blob), fileModel.fileName);
            }
          } else {
            this.errorStatus = ERRORS.PREVIEWING;
          }
        }),
        catchError( () => {
          this.errorStatus = ERRORS.LIMIT_REACHED;
          return EMPTY;
        }),
        finalize(() => {
          this.loadingStatus = '';
          this.isDocumentPreviewInProgress = false;
          this.refreshView();
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  private resetViewState(): void {
    this.currentPreviewState = '';
  }
}
